import React from 'react';
import { AssetTypeConfig } from './utils/assetTypeConfig';
import {
  FileText, Mail, Share2, Hash, HelpCircle, Megaphone,
  Image, Video, FileIcon, Calendar, User, Link, Tag
} from 'lucide-react';

interface AssetPreviewProps {
  assetType: string;
  config: AssetTypeConfig;
  formData: Record<string, any>;
}

export default function AssetPreview({
  assetType,
  config,
  formData
}: AssetPreviewProps) {
  const renderPreview = () => {
    switch (assetType) {
      case 'email':
        return (
          <div className="space-y-4">
            <div className="bg-white border rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-600">From:</span>
                    <span className="text-sm">{formData.from_name} &lt;{formData.from_email}&gt;</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-600">Subject:</span>
                    <span className="text-sm font-semibold">{formData.subject}</span>
                  </div>
                  {formData.preheader && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-600">Preview:</span>
                      <span className="text-sm text-gray-500">{formData.preheader}</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="p-4">
                <div
                  className="prose max-w-none"
                  dangerouslySetInnerHTML={{ __html: formData.html_content || '<p>No content</p>' }}
                />
              </div>
            </div>
          </div>
        );

      case 'social':
        return (
          <div className="space-y-4">
            <div className="bg-white border rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <div className="font-medium text-gray-900">Your Brand</div>
                  <div className="text-xs text-gray-500">
                    {formData.platform && `@yourbrand · ${formData.platform}`}
                  </div>
                  <div className="mt-2 text-gray-800 whitespace-pre-wrap">{formData.post_text}</div>

                  {formData.hashtags && formData.hashtags.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {formData.hashtags.map((tag: string, i: number) => (
                        <span key={i} className="text-blue-600">#{tag}</span>
                      ))}
                    </div>
                  )}

                  {formData.link && (
                    <div className="mt-3 border rounded-lg p-3 bg-gray-50">
                      <div className="flex items-center gap-2 text-sm text-blue-600">
                        <Link className="w-4 h-4" />
                        {formData.link}
                      </div>
                    </div>
                  )}

                  {formData.schedule_date && (
                    <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
                      <Calendar className="w-3 h-3" />
                      Scheduled for {formData.schedule_date} {formData.schedule_time}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );

      case 'meta':
        return (
          <div className="space-y-4">
            {/* Google Search Preview */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Google Search Preview</h4>
              <div className="bg-white border rounded-lg p-4">
                <div className="text-blue-600 text-lg hover:underline cursor-pointer">
                  {formData.title || 'Page Title'}
                </div>
                <div className="text-green-700 text-sm mt-1">{formData.page_url || 'https://example.com'}</div>
                <div className="text-gray-600 text-sm mt-1">{formData.description || 'Page description'}</div>
              </div>
            </div>

            {/* Social Media Preview */}
            {(formData.og_title || formData.og_description || formData.og_image) && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Social Media Preview</h4>
                <div className="bg-white border rounded-lg overflow-hidden max-w-md">
                  {formData.og_image && (
                    <div className="h-48 bg-gray-200 flex items-center justify-center">
                      <Image className="w-12 h-12 text-gray-400" />
                    </div>
                  )}
                  <div className="p-4">
                    <div className="font-semibold">{formData.og_title || formData.title}</div>
                    <div className="text-sm text-gray-600 mt-1">
                      {formData.og_description || formData.description}
                    </div>
                    <div className="text-xs text-gray-500 mt-2">{formData.page_url}</div>
                  </div>
                </div>
              </div>
            )}

            {/* Keywords */}
            {formData.keywords && formData.keywords.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Keywords</h4>
                <div className="flex flex-wrap gap-2">
                  {formData.keywords.map((keyword: string, i: number) => (
                    <span key={i} className="px-2 py-1 bg-gray-100 text-gray-700 rounded-md text-sm">
                      {keyword}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        );

      case 'faq':
        const questions = formData.questions ? JSON.parse(formData.questions) : [];
        return (
          <div className="space-y-4">
            <div className="bg-white border rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-3">{formData.category} FAQ</h4>
              {questions.length > 0 ? (
                <div className="space-y-3">
                  {questions.map((item: any, i: number) => (
                    <div key={i} className="border-l-4 border-blue-500 pl-4 py-2">
                      <div className="font-medium text-gray-900">{item.question}</div>
                      <div className="text-sm text-gray-600 mt-1">{item.answer}</div>
                      {item.tags && item.tags.length > 0 && (
                        <div className="flex gap-1 mt-2">
                          {item.tags.map((tag: string, j: number) => (
                            <span key={j} className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded">
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500">No questions added</p>
              )}
            </div>
          </div>
        );

      case 'ads':
        return (
          <div className="space-y-4">
            <div className="bg-white border rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-3">Google Ads Preview</h4>

              {/* Ad Preview */}
              <div className="border rounded-lg p-3 bg-gray-50">
                <div className="text-xs text-green-700 mb-1">Ad · {formData.display_url}</div>

                {/* Headlines */}
                {formData.headlines && formData.headlines.length > 0 && (
                  <div className="text-blue-600 text-lg font-medium hover:underline cursor-pointer">
                    {formData.headlines.slice(0, 3).join(' | ')}
                  </div>
                )}

                {/* Descriptions */}
                {formData.descriptions && formData.descriptions.length > 0 && (
                  <div className="text-gray-700 text-sm mt-1">
                    {formData.descriptions.join(' ')}
                  </div>
                )}

                {/* Keywords */}
                {formData.keywords && formData.keywords.length > 0 && (
                  <div className="mt-3 pt-3 border-t">
                    <div className="text-xs text-gray-500 mb-1">Target Keywords:</div>
                    <div className="flex flex-wrap gap-1">
                      {formData.keywords.map((keyword: string, i: number) => (
                        <span key={i} className="text-xs px-2 py-0.5 bg-white border rounded">
                          {keyword}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Budget */}
                {formData.daily_budget && (
                  <div className="mt-2 text-xs text-gray-500">
                    Daily Budget: ${formData.daily_budget}
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 'copy':
        return (
          <div className="bg-white border rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-3">{formData.title || 'Untitled'}</h3>
            {formData.category && (
              <span className="inline-block px-2 py-1 bg-gray-100 text-gray-700 rounded-md text-sm mb-3">
                {formData.category}
              </span>
            )}
            <div
              className="prose max-w-none"
              dangerouslySetInnerHTML={{ __html: formData.content || '<p>No content</p>' }}
            />
            {formData.tags && formData.tags.length > 0 && (
              <div className="mt-4 pt-4 border-t flex flex-wrap gap-2">
                {formData.tags.map((tag: string, i: number) => (
                  <span key={i} className="flex items-center gap-1 text-sm text-gray-600">
                    <Tag className="w-3 h-3" />
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        );

      default:
        // For file uploads, show file info
        if (config.requiresUpload && formData.file) {
          return (
            <div className="bg-white border rounded-lg p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-gray-100 rounded-lg">
                  {assetType === 'image' ? <Image className="w-8 h-8 text-gray-600" /> :
                   assetType === 'video' ? <Video className="w-8 h-8 text-gray-600" /> :
                   <FileIcon className="w-8 h-8 text-gray-600" />}
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">{formData.file.name}</h4>
                  <p className="text-sm text-gray-500">
                    {(formData.file.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                  {formData.alt_text && (
                    <p className="text-sm text-gray-600 mt-1">Alt: {formData.alt_text}</p>
                  )}
                </div>
              </div>
            </div>
          );
        }

        return (
          <div className="bg-gray-50 rounded-lg p-8 text-center text-gray-500">
            Preview not available for this asset type
          </div>
        );
    }
  };

  return (
    <div>
      <h3 className="text-lg font-medium text-gray-900 mb-4">Preview Your Asset</h3>
      <p className="text-sm text-gray-600 mb-6">
        Review how your {config.label.toLowerCase()} will appear before creating it.
      </p>

      <div className="space-y-6">
        {/* Asset Info Summary */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Asset Information</h4>
          <dl className="grid grid-cols-2 gap-2 text-sm">
            <div>
              <dt className="text-gray-500">Type:</dt>
              <dd className="font-medium">{config.label}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Storage:</dt>
              <dd className="font-medium">{config.requiresUpload ? 'File Upload' : 'Database'}</dd>
            </div>
          </dl>
        </div>

        {/* Content Preview */}
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-3">Content Preview</h4>
          {renderPreview()}
        </div>
      </div>
    </div>
  );
}